import './ox-board-player-grid.js'

import { LitElement, PropertyValues, css, html } from 'lit'
import { customElement, property, query, state } from 'lit/decorators.js'

@customElement('ox-board-player-carousel')
class BoardPlayerCarousel extends LitElement {
  static styles = [
    css`
      :host {
        display: block;
        width: 100%;
        height: 100%;
        position: relative;
        margin: 0 auto 0px;
        -webkit-perspective: 1200px;
        -moz-perspective: 1200px;
        -o-perspective: 1200px;
        perspective: 1200px;
      }

      #carousel {
        width: 100%;
        height: 100%;
        margin: 0px auto 0px;

        position: absolute;
        -webkit-transform-style: preserve-3d;
        -moz-transform-style: preserve-3d;
        -o-transform-style: preserve-3d;
        transform-style: preserve-3d;
        -webkit-transition: -webkit-transform 1.5s;
        -moz-transition: -moz-transform 1.5s;
        -o-transition: -o-transform 1.5s;
        transition: transform 1.5s;
      }

      #carousel > * {
        position: absolute;
        width: 100%;
        height: 100%;
        font-weight: bold;
        -webkit-transition:
          opacity 1.5s,
          -webkit-transform 1.5s;
        -moz-transition:
          opacity 1.5s,
          -moz-transform 1.5s;
        -o-transition:
          opacity 1.5s,
          -o-transform 1.5s;
        transition:
          opacity 1.5s,
          transform 1.5s;

        -webkit-backface-visibility: hidden;
        -moz-backface-visibility: hidden;
        -o-backface-visibility: hidden;
        backface-visibility: hidden;
      }
    `
  ]

  @property({ type: String }) axis: string = 'y'
  @property({ type: Number }) rows: number = 1
  @property({ type: Number }) columns: number = 1

  @state() _slotObserver?: MutationObserver
  @state() _boundResize?: () => void
  @state() _rotation: number = 0
  @state() _theta: number = 0
  @state() _radius: number = 0
  @state() _rotateFn?: string

  @state() _panelCount: number = 0
  @state() _panelSize: number = 0
  @state() _isHorizontal: boolean = true

  @query('#slot') _slot!: HTMLSlotElement
  @query('#carousel') _carousel!: HTMLElement

  render() {
    return html`
      <slot id="slot" select="[page]"></slot>

      <div id="carousel"></div>
    `
  }

  async connectedCallback() {
    await super.connectedCallback()

    this._boundResize = this.build.bind(this)
    window.addEventListener('resize', this._boundResize)

    this._slotObserver = new MutationObserver(mutations => {
      this.build()
    })

    await this.updateComplete

    this._slotObserver?.observe(this._slot, { childList: true })
    this.build()
  }

  disconnectedCallback() {
    super.disconnectedCallback()

    this._slotObserver?.disconnect()
    delete this._slotObserver

    if (this._boundResize) {
      window.removeEventListener('resize', this._boundResize)
      delete this._boundResize
    }
  }

  firstUpdated() {
    this._rotation = 0
  }

  updated(changes: PropertyValues<this>) {
    changes.has('axis') && this._onAxisChanged()
  }

  build() {
    var pages = Array.from(this.querySelectorAll('[page]'))
    var panel = Array.from(this._carousel.querySelectorAll('ox-board-player-grid')).pop()

    var rows = this.rows || 1
    var columns = this.columns || 1

    var i = panel ? panel.querySelectorAll('[page]').length : 0
    var page = pages.shift()

    while (page) {
      if (!(i++ % (rows * columns))) {
        panel = document.createElement('ox-board-player-grid')
        ;(panel as any).rows = rows
        ;(panel as any).columns = columns

        this._carousel.appendChild(panel)
      }
      panel?.appendChild(page)

      page = pages.shift()
    }

    this.start()
  }

  start() {
    var panels = this._carousel.querySelectorAll('ox-board-player-grid')

    this._isHorizontal = this.axis === 'y'

    this._panelCount = panels.length

    this._panelSize = this._carousel[this._isHorizontal ? 'offsetWidth' : 'offsetHeight'] || 640
    this._rotateFn = this._isHorizontal ? 'rotateY' : 'rotateX'
    this._theta = 360 / (this._panelCount || 1)

    // do some trig to figure out how big the carousel is in 3D space
    this._radius = Math.round(this._panelSize / 2 / Math.tan(Math.PI / (this._panelCount < 2 ? 2 : this._panelCount)))

    for (let i = 0; i < this._panelCount; i++) {
      let panel = panels[i] as HTMLElement
      let angle = this._theta * i
      panel.style.opacity = '1'

      panel.style.backgroundColor = 'white'

      panel.style.transform = this._rotateFn + '(' + angle + 'deg) translateZ(' + this._radius + 'px)'
    }

    // adjust rotation so panels are always flat
    this._rotation = Math.round(this._rotation / this._theta) * this._theta
    this._transform()
  }

  stop() {
    this._carousel.innerHTML = ''
  }

  _onAxisChanged() {
    this.start()
  }

  _transform() {
    // push the carousel back in 3D space, and rotate it
    this._carousel.style.transform =
      'translateZ(-' + this._radius + 'px) ' + this._rotateFn + '(' + this._rotation + 'deg)'
    this.dispatchEvent(new CustomEvent('transform', { bubbles: true, composed: true }))
  }

  previous() {
    this._rotation += this._theta
    this._transform()
  }

  next() {
    this._rotation -= this._theta
    this._transform()
  }
}
